import { useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AutomationCatalog } from '@/api/automation';
import { HowWeHelpDialog } from './HowWeHelpDialog';

interface ChooseHowWeHelpProps {
  /** When set, opens an in-place dialog; otherwise navigates to `/me/automation-help`. */
  catalog?: AutomationCatalog | null;
  /**
   * Inside a modal host, the host shows the consent step in place of its own content (DLG-005,
   * TMMAPP-0097 §C 23): the button asks for it instead of opening a dialog on top.
   */
  onRequest?: () => void;
  /** `data-confirm-opener`, so the host can return focus here after the in-place step. */
  openerKey?: string;
  onCatalogChange?: (catalog: AutomationCatalog) => void;
  onCompleted?: () => void;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

/**
 * Affordance that replaces Connect / Import while consent is unanswered — not a disabled control.
 */
export function ChooseHowWeHelpButton({
  catalog = null,
  onRequest,
  openerKey,
  onCatalogChange,
  onCompleted,
  size = 'medium',
  variant = 'contained',
}: ChooseHowWeHelpProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function handleClick() {
    if (onRequest !== undefined) {
      onRequest();
      return;
    }
    if (catalog !== null) {
      setOpen(true);
      return;
    }
    void navigate('/me/automation-help');
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={handleClick}
        {...(openerKey !== undefined ? { 'data-confirm-opener': openerKey } : {})}
      >
        {t('consent.chooseFirst')}
      </Button>
      {catalog !== null && onRequest === undefined && open && (
        <HowWeHelpDialog
          catalog={catalog}
          onCatalogChange={onCatalogChange}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onCompleted?.();
          }}
        />
      )}
    </>
  );
}
